import type { ApiClient } from './api-client';
import type { FeedbackCategory, FeedbackSeverity, DiagnosticsSnapshot } from './types';
import type { DiagnosticsCapture } from './diagnostics';
import { captureScreenshot } from './screenshot';
import { ScreenRecorder } from './recorder';

interface BuildV3Options {
  api: ApiClient;
  diagnostics: DiagnosticsCapture;
  storedEmail: string | null;
  emailCaptureEnabled: boolean;
  metadata: Record<string, unknown>;
  resumedSession?: any;
  onSubmitted: (feedbackId: string) => void;
  onSessionStarted?: (sessionId: string) => void;
  onSwitchToStandardForm: (prefill?: {
    title?: string;
    description?: string;
    category?: FeedbackCategory;
    severity?: FeedbackSeverity;
  }) => void;
}

interface BotMessageView {
  role: 'user' | 'assistant';
  content: string;
  captureUrl?: string;
}

function stripControlTokens(text: string): string {
  return text
    .replace(/\[READY_TO_FINALIZE\]/g, '')
    .replace(/\[REQUEST_CAPTURE:(screenshot|video)\]/g, '')
    .trim();
}

function getCaptureErrorStatus(error: unknown): string {
  if (error instanceof Error && error.message) {
    return `Capture failed: ${error.message}. You can continue without media.`;
  }
  return 'Capture failed. You can continue without media.';
}

export function buildV3BotModal(options: BuildV3Options): {
  form: HTMLDivElement;
} {
  const container = document.createElement('div');
  container.className = 'fh-bot-container';

  const privacy = document.createElement('p');
  privacy.className = 'fh-bot-privacy';
  privacy.textContent = 'This conversation is recorded and stored for bug triage.';
  container.appendChild(privacy);

  const thread = document.createElement('div');
  thread.className = 'fh-bot-thread';
  container.appendChild(thread);

  const emailRow = document.createElement('div');
  emailRow.className = 'fh-field';
  if (options.emailCaptureEnabled) {
    emailRow.innerHTML = `
      <label>Email (optional — to receive replies)</label>
      <input class="fh-bot-email" type="email" placeholder="you@example.com" value="${options.storedEmail ?? ''}" />
    `;
    container.appendChild(emailRow);
  }

  const inputRow = document.createElement('div');
  inputRow.className = 'fh-bot-input-row';
  inputRow.innerHTML = `
    <textarea class="fh-bot-input" placeholder="Describe the issue..." rows="2"></textarea>
    <button class="fh-submit-btn fh-bot-send" type="button">Send</button>
  `;
  container.appendChild(inputRow);

  const actionRow = document.createElement('div');
  actionRow.className = 'fh-bot-actions';
  actionRow.innerHTML = `
    <button class="fh-capture-btn fh-bot-capture" data-type="screenshot" type="button">Attach Screenshot</button>
    <button class="fh-capture-btn fh-bot-capture" data-type="video" type="button">Attach Video</button>
    <button class="fh-submit-btn fh-bot-finalize" type="button" disabled>Submit Feedback</button>
    <button class="fh-capture-btn fh-bot-fallback" type="button">Switch to standard form</button>
  `;
  container.appendChild(actionRow);

  const status = document.createElement('div');
  status.className = 'fh-bot-status';
  container.appendChild(status);

  let sessionId = '';
  let readyToFinalize = false;
  let pendingAssistantBubble: HTMLDivElement | null = null;
  let pendingAssistantText = '';

  const input = inputRow.querySelector('.fh-bot-input') as HTMLTextAreaElement;
  const sendBtn = inputRow.querySelector('.fh-bot-send') as HTMLButtonElement;
  const finalizeBtn = actionRow.querySelector('.fh-bot-finalize') as HTMLButtonElement;
  const fallbackBtn = actionRow.querySelector('.fh-bot-fallback') as HTMLButtonElement;
  const captureButtons = actionRow.querySelectorAll('.fh-bot-capture') as NodeListOf<HTMLButtonElement>;
  const emailInput = container.querySelector('.fh-bot-email') as HTMLInputElement | null;

  const setBusy = (busy: boolean) => {
    sendBtn.disabled = busy;
    input.disabled = busy;
    captureButtons.forEach((btn) => {
      btn.disabled = busy || !sessionId;
    });
  };

  const appendMessage = (message: BotMessageView): HTMLDivElement => {
    const bubble = document.createElement('div');
    bubble.className = `fh-bot-message ${message.role}`;
    bubble.textContent = message.content;
    thread.appendChild(bubble);

    if (message.captureUrl) {
      const img = document.createElement('img');
      img.className = 'fh-bot-capture-preview';
      img.src = message.captureUrl;
      img.alt = 'Captured media preview';
      thread.appendChild(img);
    }

    thread.scrollTop = thread.scrollHeight;
    return bubble;
  };

  const ensureSession = async (): Promise<void> => {
    if (sessionId) return;
    setBusy(true);
    try {
      // Check if we have a resumed session
      if (options.resumedSession) {
        sessionId = options.resumedSession.sessionId;
        // Load previous messages
        if (options.resumedSession.messages) {
          for (const msg of options.resumedSession.messages) {
            appendMessage({
              role: msg.role,
              content: msg.content,
              captureUrl: msg.captureUrl,
            });
          }
        }
        options.onSessionStarted?.(sessionId);
        status.textContent = '';
      } else {
        // Start new session
        const start = await options.api.startBotSession({
          testerEmail: emailInput?.value?.trim() || undefined,
          metadata: options.metadata,
        });
        sessionId = start.sessionId;
        options.onSessionStarted?.(sessionId);
        appendMessage({ role: 'assistant', content: start.greeting });
        status.textContent = '';
      }
    } catch {
      status.textContent = 'Bot is unavailable right now.';
      options.onSwitchToStandardForm();
    } finally {
      setBusy(false);
    }
  };

  const sendTurn = async (): Promise<void> => {
    const text = input.value.trim();
    if (!text) return;

    await ensureSession();
    if (!sessionId) return;

    appendMessage({ role: 'user', content: text });
    input.value = '';
    pendingAssistantText = '';
    pendingAssistantBubble = appendMessage({ role: 'assistant', content: '' });

    setBusy(true);
    status.textContent = 'Assistant is typing...';

    try {
      await options.api.streamBotTurn(sessionId, text, {
        onDelta: (delta) => {
          pendingAssistantText += delta;
          if (pendingAssistantBubble) {
            pendingAssistantBubble.textContent = stripControlTokens(pendingAssistantText) || '...';
            thread.scrollTop = thread.scrollHeight;
          }

          if (pendingAssistantText.includes('[REQUEST_CAPTURE:screenshot]')) {
            status.textContent = 'Assistant requested a screenshot.';
          }
          if (pendingAssistantText.includes('[REQUEST_CAPTURE:video]')) {
            status.textContent = 'Assistant requested a video.';
          }
        },
        onReady: () => {
          readyToFinalize = true;
          finalizeBtn.disabled = false;
          status.textContent = 'Ready to submit.';
        },
        onError: () => {
          status.textContent = 'Bot stream failed. You can switch to the standard form.';
          fallbackBtn.classList.add('fh-bot-fallback-visible');
        },
        onDone: () => {
          if (!readyToFinalize) {
            status.textContent = '';
          }
        },
      });
    } catch {
      status.textContent = 'Bot stream failed. You can switch to the standard form.';
      fallbackBtn.classList.add('fh-bot-fallback-visible');
    } finally {
      setBusy(false);
    }
  };

  const attachCapture = async (captureType: 'screenshot' | 'video'): Promise<void> => {
    if (!sessionId) {
      await ensureSession();
      if (!sessionId) return;
    }

    setBusy(true);
    status.textContent = 'Preparing capture...';

    try {
      let blob: Blob;
      if (captureType === 'screenshot') {
        const widgetRoot = document.getElementById('fh-widget-root');
        if (widgetRoot) widgetRoot.style.display = 'none';

        let canvas: HTMLCanvasElement;
        try {
          await new Promise((resolve) => setTimeout(resolve, 150));
          canvas = await captureScreenshot();
        } finally {
          if (widgetRoot) widgetRoot.style.display = '';
        }

        blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((result) => {
            if (result) resolve(result);
            else reject(new Error('Unable to capture screenshot.'));
          }, 'image/png');
        });
      } else {
        const recorder = new ScreenRecorder(60);
        blob = await new Promise<Blob>((resolve, reject) => {
          recorder.onStop = (recordedBlob) => resolve(recordedBlob);
          recorder.onError = (err) => reject(err);
          recorder.start()
            .then(() => {
              setTimeout(() => {
                try {
                  recorder.stop();
                } catch (err) {
                  reject(err as Error);
                }
              }, 5000);
            })
            .catch((err) => reject(err instanceof Error ? err : new Error(String(err))));
        });
      }

      const uploaded = await options.api.attachBotCapture(sessionId, blob, captureType);
      appendMessage({
        role: 'user',
        content: captureType === 'screenshot' ? 'Attached a screenshot.' : 'Attached a video.',
        captureUrl: uploaded.captureUrl,
      });
      status.textContent = 'Capture attached.';
    } catch (err) {
      console.warn('[FeedbackHub] Capture error:', err);
      status.textContent = getCaptureErrorStatus(err);
    } finally {
      setBusy(false);
    }
  };

  const finalize = async (): Promise<void> => {
    if (!sessionId) return;

    setBusy(true);
    finalizeBtn.disabled = true;
    status.textContent = 'Submitting feedback...';
    try {
      const diagnosticsSnapshot = options.diagnostics.snapshot();
      const currentEmail = emailInput?.value?.trim() || undefined;
      const { feedbackId } = await options.api.finalizeBot(sessionId, diagnosticsSnapshot, currentEmail);
      status.textContent = '';
      options.onSubmitted(feedbackId);
    } catch {
      status.textContent = 'Failed to finalize. You can continue chatting or switch to the standard form.';
      finalizeBtn.disabled = !readyToFinalize;
    } finally {
      setBusy(false);
    }
  };

  sendBtn.addEventListener('click', () => {
    sendTurn();
  });
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendTurn();
    }
  });

  finalizeBtn.addEventListener('click', () => {
    finalize();
  });

  fallbackBtn.addEventListener('click', () => {
    options.onSwitchToStandardForm();
  });

  captureButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const type = button.dataset.type === 'video' ? 'video' : 'screenshot';
      attachCapture(type);
    });
  });

  ensureSession();

  return { form: container };
}
